import type { OrderException, PutawayTaskView } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../api';
import { mappable, shownReason, unmatchedLines, unmatchedText } from '../exceptions';
import { fmt } from '../format';
import { useSession } from '../session';
import type { AllocationOutcome } from '../types';
import { ChannelBadge, Empty, ErrorBanner, Modal, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { pickSeller } from './catalog/SellerPick';
import { toteText } from './PickRoutes';
import { Icon } from '../ui/icons';

/** The supervisor's queue: orders the floor could not finish, with what is missing and where the rest waits. */
export function Exceptions({ onChange }: { onChange: () => void }) {
  const { warehouseId, isSupervisor } = useSession();
  const toast = useToast();
  const list = useLoad(() => api<OrderException[]>('GET', `/api/wms/exceptions${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], 15_000);
  const [cancelling, setCancelling] = useState<OrderException | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const done = () => {
    void list.reload();
    onChange();
  };

  const retry = async (e: OrderException) => {
    setBusy(e.orderId);
    try {
      const outcome = await api<AllocationOutcome>('POST', `/api/wms/exceptions/${e.orderId}/retry`);
      toast(
        outcome.status === 'allocated'
          ? e.desk
            ? `${e.orderNumber}: eksik ürün ayrıldı, toplamaya hazır; toplanınca sipariş paketleme masasına döner`
            : `${e.orderNumber}: eksik ürünler başka gözlerden ayrıldı, toplamaya hazır`
          : `${e.orderNumber}: stok yetersiz (${outcome.shortages.map((s) => `${s.sku} −${s.missing}`).join(', ')}); stok gelince otomatik ayrılacak`,
        outcome.status === 'allocated' ? 'ok' : 'info',
      );
      done();
    } catch (err) {
      toast(errorMessage(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  if (list.error) return <ErrorBanner message={list.error} onRetry={() => void list.reload()} />;
  if (!list.data) return <Spinner />;
  if (list.data.length === 0) {
    return (
      <section className="card">
        <Empty title="Sorunlu sipariş yok">Eksik toplanan, paketlemede eksik çıkan, ürünü eşleşmeyen ya da pazaryerinde bulunamayan siparişler burada listelenir.</Empty>
      </section>
    );
  }

  return (
    <>
      {!isSupervisor && <div className="banner info">Karar vermek için vardiya sorumlusu veya depo yöneticisi olarak giriş yapılmalı.</div>}
      {list.data.map((e) => {
        // a line no product matches: allocation needs a product first, so the seller's mapping helps, not a retry
        const unmatched = unmatchedLines(e);
        const reason = shownReason(e.holdReason, unmatched.length > 0);
        return (
          <section key={e.orderId} className="card">
            <div className="card-h">
              <h2 className="mono">{e.orderNumber}</h2>
              <SellerChip code={e.tenantCode} />
              <ChannelBadge channel={e.channel} />
              <span className="sub">{fmt.age(e.heldSince)} önce beklemeye alındı</span>
              <span className="spacer" />
              {isSupervisor && (
                <>
                  <button className="btn" disabled={busy === e.orderId} onClick={() => setCancelling(e)}>
                    İptal et
                  </button>
                  {unmatched.length > 0 ? (
                    // the catalog screens are the managers' (the API refuses the floor staff): the seller's mapping opens.
                    // A line with nothing to map it by never shows there and the order cannot go on: the text alone
                    unmatched.every(mappable) && (
                      <a className="btn primary" href="#/urunler/eslestirme" onClick={() => pickSeller(e.tenantId)}>
                        <Icon name="link" size={18} /> Eşleştirmeye git
                      </a>
                    )
                  ) : (
                    <button className="btn primary" disabled={busy === e.orderId} onClick={() => void retry(e)}>
                      Eksikleri yeniden ayır
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="card-b stack">
              {reason && <div className="banner warn">{reason}</div>}
              {unmatched.length > 0 && <div className="banner warn">{unmatchedText(unmatched, reason !== null)}</div>}
              {/* held at a packing desk: the parcel and its scans wait for the missing units */}
              {e.desk && (
                <div className="row">
                  <span className="chip warn">
                    <Icon name="box" size={14} /> Koli BEKLEYEN rafında
                  </span>
                  <span className="muted">
                    Masa <span className="mono">{e.desk.stationCode}</span> · {e.desk.packedUnits} adet okutuldu
                  </span>
                </div>
              )}
              <div className="table-wrap">
                <table className="t stack-sm">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th className="num">Sipariş</th>
                      <th className="num">Gözde ayrılmış</th>
                      <th className="num">Toplanan</th>
                      <th className="num">Eksik</th>
                    </tr>
                  </thead>
                  <tbody>
                    {e.lines.map((l) => (
                      <tr key={l.orderItemId}>
                        <td>
                          <b>{l.name}</b> <span className="mono muted">{l.sku ?? l.barcode}</span>
                          {l.unmatched && (
                            <span className="chip warn" style={{ marginLeft: 6 }}>
                              Eşleşmedi
                            </span>
                          )}
                        </td>
                        <td className="num" data-label="Sipariş">{l.ordered}</td>
                        <td className="num" data-label="Gözde ayrılmış">{l.reserved}</td>
                        <td className="num" data-label="Toplanan">{l.picked}</td>
                        <td className="num" data-label="Eksik">{l.missing > 0 ? <span className="chip bad">{l.missing}</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {e.totes.length > 0 && (
                <div className="row">
                  <span className="muted">Toplanan ürünler:</span>
                  {e.totes.map((t) => (
                    <span key={`${t.routeId}-${t.toteSlot}`} className="chip">
                      {toteText(t)} · {t.units} adet
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })}
      {cancelling && (
        <CancelDialog
          exception={cancelling}
          onClose={() => setCancelling(null)}
          onDone={(tasks) => {
            setCancelling(null);
            toast(
              tasks.length > 0 ? `${cancelling.orderNumber} iptal edildi; ${tasks.length} rafa iade görevi açıldı` : `${cancelling.orderNumber} iptal edildi`,
              'ok',
            );
            done();
          }}
        />
      )}
    </>
  );
}

function CancelDialog(props: { exception: OrderException; onClose: () => void; onDone: (tasks: readonly PutawayTaskView[]) => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const result = await api<{ putBackTasks: PutawayTaskView[] }>('POST', `/api/wms/exceptions/${props.exception.orderId}/cancel`, { reason });
      props.onDone(result.putBackTasks);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${props.exception.orderNumber} iptal edilsin mi?`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn danger" disabled={busy || reason.trim().length < 3} onClick={() => void submit()}>
            Siparişi iptal et
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <p>Toplanmış ürünler paketleme alanına alınır ve geldikleri göze rafa iade görevi açılır. Satıcıya bilgi verin.</p>
        {props.exception.desk && <p>Koli BEKLEYEN rafında: koliyi açın, içindekiler bu görevlerle rafa döner.</p>}
        <div className="field">
          <label htmlFor="reason">Gerekçe</label>
          <textarea id="reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Örn. satıcı stok bulamadı, siparişi iptal etti" />
        </div>
      </div>
    </Modal>
  );
}
